import React, { useState, useMemo, useCallback } from 'react';
import { Button, Modal, Card, Spinner, Row, Col } from 'react-bootstrap';
import { FiDownload, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import axios from 'axios';
import IndividualReportDownload from './IndividualReportDownload';
import BulkReportDownload from './BulkReportDownload';

const ReportViewDownload = ({
    selectedRows = [],
    selectedStatusCounts = { Paid: 0, Pending: 0, Unpaid: 0 },
    totalSelectedDeductionAmount = 0,
    formatCurrency,
    getProductName,
    getBankAccountNumber,
    onClearSelection,
    productCache = {},
    bankAccountCache = {}
}) => {
    const [showPreview, setShowPreview] = useState(false);
    const [viewMode, setViewMode] = useState('summary');
    const [isUploading, setIsUploading] = useState(false);

    const safeSelectedRows = selectedRows || [];
    const hasSelectedRows = safeSelectedRows.length > 0;

    const currencyFormatter = useMemo(() => {
        return formatCurrency || ((amount) => {
            if (!amount && amount !== 0) return "S$0.00";
            return new Intl.NumberFormat("en-SG", {
                style: "currency",
                currency: "SGD",
            }).format(amount);
        });
    }, [formatCurrency]);

    const calculateEMIDetails = useCallback((row) => {
        try {
            const billAmount = parseFloat(row.bill_amount) || 0;
            const emiPlan = parseInt(row.emi_plan_duration) || 1;
            const reports = Array.isArray(row.reports) ? row.reports : [];
            const currentMonth = moment().tz("Asia/Singapore").format("MMM YYYY");

            const monthlyEMI = billAmount / emiPlan;
            const paidMonths = reports.filter(r => r.status === "Paid").length;
            const paidAmount = paidMonths * monthlyEMI;
            const pendingAmount = billAmount - paidAmount;
            const currentMonthReport = reports.find(r => r.month === currentMonth);

            return {
                monthlyEMI,
                paidAmount,
                pendingAmount,
                paidMonths,
                totalMonths: emiPlan,
                currentMonthStatus: currentMonthReport?.status || "Not started",
                progressPercentage: emiPlan > 0 ? (paidMonths / emiPlan) * 100 : 0
            };
        } catch (error) {
            return {
                monthlyEMI: 0,
                paidAmount: 0,
                pendingAmount: 0,
                paidMonths: 0,
                totalMonths: 0,
                currentMonthStatus: "Error",
                progressPercentage: 0
            };
        }
    }, []);

    const generateReportContent = useMemo(() => {
        if (!hasSelectedRows) {
            return 'No customers selected for report generation.';
        }

        const today = moment().tz("Asia/Singapore");
        const fileDate = today.format('DDMMYYYY');
        const constantBankAccount = '0721154749';
        const currentMonth = today.format("MMM YYYY");

        let content = '';
        let totalDeductionAmount = 0;
        let processedCount = 0;

        content += `HEADER,${fileDate},SGAZZE01,AZZ ENTERPRISES\n`;

        safeSelectedRows.forEach((row, index) => {
            try {
                const { monthlyEMI, paidMonths } = calculateEMIDetails(row);
                const reports = Array.isArray(row.reports) ? row.reports : [];
                const currentMonthReport = reports.find(r => r.month === currentMonth);

                if (currentMonthReport?.status === "Paid") {
                    return;
                }

                const bankAccount = getBankAccountNumber ? getBankAccountNumber(row.boDDARefNo) : "0000";
                const applicantBankAccNo = bankAccount && bankAccount !== "—" && bankAccount !== "Loading..."
                    ? bankAccount
                    : "0000";

                const transactionId = `AZZ${today.format('DDMMYYYY')}${(processedCount + 1).toString().padStart(3, '0')}`;

                const installmentNumber = paidMonths + 1;

                const customerName = row.full_name || "Customer";
                const descriptiveName = `${customerName} Collection Installment ${installmentNumber}`;

                totalDeductionAmount += Math.round(monthlyEMI);
                processedCount++;

                content += `PAYMENT,GPC,${constantBankAccount},SGD,${transactionId},SGD,13871,${fileDate},,,${customerName},,,,,${applicantBankAccNo},,,,,DBSSSGSGXXX,,,,,,,${Math.round(monthlyEMI)},,,,,,,${row.boDDARefNo || `REF${index + 1}`},${descriptiveName},,,,,,,SUPP\n`;

            } catch (error) {
                content += `PAYMENT,ERROR PROCESSING RECORD${','.repeat(50)}\n`;
            }
        });

        content += `TRAILER,${processedCount},${totalDeductionAmount}`;

        return content;
    }, [hasSelectedRows, safeSelectedRows, calculateEMIDetails, getBankAccountNumber]);

    const uploadFileToS3 = async (fileContent, fileName) => {
        try {
            const blob = new Blob([fileContent], { type: 'text/plain' });
            const file = new File([blob], fileName, { type: 'text/plain' });

            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post('https://www.azzenterprises.com/api/aws/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                withCredentials: true
            });

            return response.data;
        } catch (error) {
            console.error('Upload error:', error);
            throw new Error(error.response?.data?.error || 'Upload failed');
        }
    };

    const saveFileUrlToDatabase = async (fileData) => {
        try {
            const response = await axios.post('https://www.azzenterprises.com/api/reports/recent',
                {
                    fileUrl: fileData.url,
                    totalAmount: totalSelectedDeductionAmount,
                    generatedAt: new Date().toISOString(),
                    customers: safeSelectedRows
                },
                { withCredentials: true }
            );
            return response.data;
        } catch (error) {
            console.error('Save to database error:', error);
            throw new Error(error.response?.data?.error || 'Failed to save file record');
        }
    };

    const handleDownload = useCallback(async () => {
        if (!hasSelectedRows) {
            toast.warn("No customers selected for download");
            return;
        }

        setIsUploading(true);

        try {
            const content = generateReportContent;
            const fileName = `Fast3-${moment().format('YYYY-MM-DD')}.txt`;

            const uploadResult = await uploadFileToS3(content, fileName);

            toast.info("Saving file record");
            await saveFileUrlToDatabase(uploadResult);

            const blob = new Blob([content], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            toast.success(`Report downloaded and saved to cloud. ${safeSelectedRows.length} customers processed`);

            setTimeout(() => {
                handleClearSelection();
            }, 500);
        } catch (error) {
            console.error('Download/Upload error:', error);
            toast.error(`Operation failed: ${error.message}`);
        } finally {
            setIsUploading(false);
        }
    }, [hasSelectedRows, safeSelectedRows.length, generateReportContent, totalSelectedDeductionAmount]);

    const handlePreview = useCallback(() => {
        if (!hasSelectedRows) {
            toast.warn("Please select customers to preview");
            return;
        }
        setShowPreview(true);
    }, [hasSelectedRows]);

    const handleClearSelection = useCallback(() => {
        onClearSelection?.();
    }, [onClearSelection]);

    if (!hasSelectedRows) {
        return null;
    }

    return (
        <>
            <SummaryView
                selectedRows={safeSelectedRows}
                selectedStatusCounts={selectedStatusCounts}
                totalSelectedDeductionAmount={totalSelectedDeductionAmount}
                currencyFormatter={currencyFormatter}
                onPreview={handlePreview}
                onDownload={handleDownload}
                onClear={handleClearSelection}
                isUploading={isUploading}
                productCache={productCache}
                bankAccountCache={bankAccountCache}
                formatCurrency={formatCurrency}
                getProductName={getProductName}
                getBankAccountNumber={getBankAccountNumber}
            />

            <PreviewModal
                show={showPreview}
                onClose={() => setShowPreview(false)}
                selectedRows={safeSelectedRows}
                selectedStatusCounts={selectedStatusCounts}
                totalSelectedDeductionAmount={totalSelectedDeductionAmount}
                currencyFormatter={currencyFormatter}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                generateReportContent={generateReportContent}
                calculateEMIDetails={calculateEMIDetails}
                getProductName={getProductName}
                onDownload={handleDownload}
                isUploading={isUploading}
                productCache={productCache}
                bankAccountCache={bankAccountCache}
                formatCurrency={formatCurrency}
                getBankAccountNumber={getBankAccountNumber}
            />
        </>
    );
};

const SummaryView = React.memo(({
    selectedRows,
    selectedStatusCounts,
    totalSelectedDeductionAmount,
    currencyFormatter,
    onPreview,
    onDownload,
    onClear,
    isUploading,
    productCache,
    bankAccountCache,
    formatCurrency,
    getProductName,
    getBankAccountNumber
}) => {
    const getStatusColor = (status) => {
        return status === "Paid"
            ? "#16a34a"
            : status === "Unpaid"
                ? "#dc2626"
                : status === "Pending"
                    ? "#d97706"
                    : "#6b7280";
    };

    const calculateEMIDetails = (row) => {
        const billAmount = parseFloat(row.bill_amount) || 0;
        const emiPlan = parseInt(row.emi_plan_duration) || 1;
        const reports = row.reports || [];

        const monthlyEMI = billAmount / emiPlan;
        const paidMonths = reports.filter(r => r.status === "Paid").length;
        const currentMonth = moment().tz("Asia/Singapore").format("MMM YYYY");
        const currentMonthReport = reports.find(r => r.month === currentMonth);

        return {
            monthlyEMI,
            currentMonthStatus: currentMonthReport?.status || "Not started"
        };
    };

    return (
        <div className="space-y-3">
            <Card className="border-0 bg-light">
                <Card.Body className="py-2">
                    <Row className="align-items-center">
                        <Col md={6}>
                            <small className="text-muted">
                                Select individual customers below to download their specific PDF reports
                            </small>
                        </Col>
                        <Col md={6}>
                            <div className="d-flex justify-content-end gap-2 flex-wrap">
                                <BulkReportDownload
                                    selectedRows={selectedRows}
                                    selectedStatusCounts={selectedStatusCounts}
                                    totalSelectedDeductionAmount={totalSelectedDeductionAmount}
                                    formatCurrency={formatCurrency}
                                    getProductName={getProductName}
                                    getBankAccountNumber={getBankAccountNumber}
                                    productCache={productCache}
                                    bankAccountCache={bankAccountCache}
                                    onClearSelection={onClear}
                                />

                                <Button
                                    variant="success"
                                    size="sm"
                                    className="d-flex align-items-center gap-1"
                                    onClick={onPreview}
                                    disabled={isUploading}
                                    style={{ fontSize: '12px' }}
                                >
                                    <FiDownload size={14} />
                                    Collection Report
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {selectedRows.length > 0 && (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-0">
                        <div className="p-3 border-bottom bg-light">
                            <h6 className="fw-bold mb-0" style={{ fontSize: '14px' }}>Selected Customers - Individual PDF Reports</h6>
                        </div>
                        <div className="p-3">
                            <div className="d-flex flex-wrap gap-2">
                                {selectedRows.map((row, index) => {
                                    return (
                                        <div key={index}>
                                            <IndividualReportDownload
                                                customer={row}
                                                productCache={productCache}
                                                bankAccountCache={bankAccountCache}
                                                formatCurrency={formatCurrency}
                                                getProductName={getProductName}
                                                getBankAccountNumber={getBankAccountNumber}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            )}
        </div>
    );
});

// Helper functions needed in this component
const getStatusColor = (status) => {
    switch (status) {
        case "Paid": return "#16a34a";
        case "Pending": return "#d97706";
        case "Unpaid": return "#dc2626";
        default: return "#6b7280";
    }
};

const PreviewModal = React.memo(({
    show,
    onClose,
    selectedRows,
    selectedStatusCounts,
    totalSelectedDeductionAmount,
    currencyFormatter,
    viewMode,
    onViewModeChange,
    generateReportContent,
    calculateEMIDetails,
    getProductName,
    onDownload,
    isUploading
}) => {
    const getStatusColor = (status) => {
        return status === "Paid"
            ? "#16a34a"
            : status === "Unpaid"
                ? "#dc2626"
                : status === "Pending"
                    ? "#d97706"
                    : "#6b7280";
    };

    const formatDueDate = useCallback((paymentDate) => {
        if (!paymentDate) return { fullDate: "No date", isOverdue: false };
        const baseDate = moment(paymentDate).tz("Asia/Singapore");
        if (!baseDate.isValid()) return { fullDate: "Invalid date", isOverdue: false };

        const currentMonthDue = moment().tz("Asia/Singapore").date(baseDate.date());
        const today = moment().tz("Asia/Singapore");
        const dueDateThisMonth = moment().tz("Asia/Singapore")
            .date(baseDate.date())
            .hour(23).minute(59).second(59);

        return {
            fullDate: currentMonthDue.format("DD MMM YYYY"),
            isOverdue: today.isAfter(dueDateThisMonth, 'day')
        };
    }, []);

    return (
        <Modal show={show} onHide={onClose} size="xl" centered className="modern-modal">
            <Modal.Header className="bg-light border-bottom-0 pb-2 position-relative">
                <div className="w-100">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                            <Modal.Title className="fw-bold text-dark mb-1" style={{ fontSize: '16px' }}>
                                Report Preview
                            </Modal.Title>
                            <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
                                {selectedRows?.length || 0} customer(s) selected
                            </p>
                        </div>
                        <Button
                            variant="link"
                            className="position-absolute top-0 end-0 p-2 m-1 text-muted"
                            onClick={onClose}
                            style={{ fontSize: '14px' }}
                            disabled={isUploading}
                        >
                            <FiX size={16} />
                        </Button>
                    </div>

                    <div className="d-flex gap-3 align-items-center bg-white p-2 rounded border">
                        <div className="text-center">
                            <div className="text-muted mb-1" style={{ fontSize: '12px' }}>Total Deduction</div>
                            <div className="fw-bold text-success" style={{ fontSize: '14px' }}>{currencyFormatter(totalSelectedDeductionAmount)}</div>
                        </div>
                        <div className="vr"></div>
                        <div className="text-center">
                            <div className="text-muted mb-1" style={{ fontSize: '12px' }}>Paid</div>
                            <div className="fw-bold" style={{ fontSize: '14px', color: getStatusColor("Paid") }}>{selectedStatusCounts?.Paid || 0}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-muted mb-1" style={{ fontSize: '12px' }}>Pending</div>
                            <div className="fw-bold" style={{ fontSize: '14px', color: getStatusColor("Pending") }}>{selectedStatusCounts?.Pending || 0}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-muted mb-1" style={{ fontSize: '12px' }}>Unpaid</div>
                            <div className="fw-bold" style={{ fontSize: '14px', color: getStatusColor("Unpaid") }}>{selectedStatusCounts?.Unpaid || 0}</div>
                        </div>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body className="pt-3">
                <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white border-0 pb-2">
                        <div className="d-flex justify-content-end align-items-center">
                            <div className="d-flex gap-1">
                                <Button
                                    variant={viewMode === 'summary' ? 'primary' : 'outline-primary'}
                                    size="sm"
                                    style={{ fontSize: '12px', padding: '4px 8px' }}
                                    onClick={() => onViewModeChange('summary')}
                                    disabled={isUploading}
                                >
                                    Summary
                                </Button>
                                <Button
                                    variant={viewMode === 'detailed' ? 'primary' : 'outline-primary'}
                                    size="sm"
                                    style={{ fontSize: '12px', padding: '4px 8px' }}
                                    onClick={() => onViewModeChange('detailed')}
                                    disabled={isUploading}
                                >
                                    Detailed
                                </Button>
                            </div>
                        </div>
                    </Card.Header>
                    <Card.Body style={{
                        maxHeight: '400px',
                        overflowY: 'auto',
                        backgroundColor: viewMode === 'detailed' ? '#fafbfc' : '#fafbfc',
                        fontFamily: viewMode === 'detailed' ? 'Monaco, Consolas, monospace' : 'inherit',
                        fontSize: viewMode === 'detailed' ? '12px' : 'inherit',
                        whiteSpace: viewMode === 'detailed' ? 'pre-wrap' : 'normal',
                        lineHeight: viewMode === 'detailed' ? '1.4' : 'normal',
                        color: viewMode === 'detailed' ? '#000' : 'inherit',
                        borderRadius: '6px',
                        padding: viewMode === 'detailed' ? '12px' : '16px'
                    }}>
                        {viewMode === 'summary' ? (
                            <div className="row g-2">
                                {selectedRows?.map((row, index) => {
                                    const { monthlyEMI, currentMonthStatus } = calculateEMIDetails(row);
                                    const { fullDate, isOverdue } = formatDueDate(row.payment_date);

                                    return (
                                        <div key={`${row.customer_id}-${index}`} className="col-12 col-md-6 col-lg-4">
                                            <Card className="h-100 border-0 shadow-sm">
                                                <Card.Body className="p-2">
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <div className="flex-grow-1">
                                                            <div className="fw-bold text-dark mb-1" style={{ fontSize: '13px' }}>{row.full_name || "N/A"}</div>
                                                            <div className="text-muted mb-1" style={{ fontSize: '11px' }}>
                                                                {getProductName ? getProductName(row.product_id) : "—"}
                                                            </div>
                                                        </div>
                                                        <span
                                                            className="badge"
                                                            style={{
                                                                backgroundColor: getStatusColor(currentMonthStatus),
                                                                color: "#fff",
                                                                fontSize: '10px'
                                                            }}
                                                        >
                                                            {currentMonthStatus}
                                                        </span>
                                                    </div>

                                                    <div className="mb-1">
                                                        <div className="text-muted" style={{ fontSize: '11px' }}>DDA Reference</div>
                                                        <div className="fw-semibold" style={{ fontSize: '12px' }}>{row.boDDARefNo || "—"}</div>
                                                    </div>

                                                    <div className="mb-2">
                                                        <div className="text-muted" style={{ fontSize: '11px' }}>Monthly EMI</div>
                                                        <div className="fw-bold text-success" style={{ fontSize: '12px' }}>{currencyFormatter(monthlyEMI)}</div>
                                                    </div>

                                                    <div className="pt-1 border-top">
                                                        <div className={`fw-semibold ${isOverdue ? 'text-danger' : 'text-muted'}`} style={{ fontSize: '11px' }}>
                                                            Due: {fullDate} {isOverdue && '⏰'}
                                                        </div>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <pre className="mb-0" style={{
                                fontFamily: 'inherit',
                                fontSize: '12px',
                                lineHeight: '1.3'
                            }}>
                                {generateReportContent}
                            </pre>
                        )}
                    </Card.Body>
                    <Card.Footer className="bg-light border-0 pt-2">
                        <div className="d-flex justify-content-between align-items-center">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                style={{ fontSize: '12px', padding: '4px 8px' }}
                                onClick={onClose}
                                disabled={isUploading}
                            >
                                Close
                            </Button>
                            <div className="d-flex gap-1">
                                <Button
                                    variant="primary"
                                    size="sm"
                                    style={{ fontSize: '12px', padding: '4px 8px' }}
                                    onClick={onDownload}
                                    disabled={isUploading}
                                >
                                    {isUploading ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-1" />
                                            Uploading...
                                        </>
                                    ) : (
                                        <>
                                            <FiDownload size={12} className="me-1" />
                                            Download Collection Report
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </Card.Footer>
                </Card>
            </Modal.Body>
        </Modal>
    );
});

export default ReportViewDownload;